import React, { useState, useEffect } from "react"
import { useIntl } from "react-intl"
import AsyncMessageResponseHandler from "components/Shared/AsyncMessageResponseHandler"
import ChartContainer from "components/Shared/ChartContainer"
import { useFetchData } from "components/Shared/Hooks/react-query/useFetchData"
import { FiltersType } from "components/Shared/GlobalFilter/context/interfaces"
import { PSYCHOGRAPHICS } from "components/Shared/GlobalFilter/constants"
import Cards from "components/CustomerBehavior/CustomerProfiling/Cards"
import {
	CustomerProfilingTable,
	CustomerProfilingTimeline,
	getCustomerProfilingTableMetric,
	getCustomerProfilingTimelineMetric,
} from "../../service"
import { dataTimelineDefault, placeholderText } from "../../Shared/utils"
import { MetricsBySegments } from "components/CustomerBehavior/CustomerProfiling/MetricsBySegments"
import Timeline from "components/Shared/Timeline"
import { Select } from "components/Shared/Select"
import {
	WEEKLY_INTERVAL,
	formatHabits,
} from "components/Shared/Timeline/constants"
import {
	options,
	TRANSACTIONS,
} from "components/CustomerBehavior/CustomerProfiling/constants"
import Tooltip from "components/CustomerBehavior/CustomerProfiling/Shared/Tooltip"
import { DateInterval } from "components/Shared/PhronesisCalendar/PhronesisCalendar.utils"

interface HabitsSegmentationProps {
	period: DateInterval
	filters: FiltersType
}

const HabitsSegmentation: React.FC<HabitsSegmentationProps> = ({
	period,
	filters,
}) => {
	const intl = useIntl()
	const [localPeriod, setLocalPeriod] = useState<DateInterval>(period)
	const [interval, setInterval] = useState<string>(WEEKLY_INTERVAL)
	const [metric, setMetric] = useState<string>(TRANSACTIONS)

	useEffect(() => {
		setInterval(WEEKLY_INTERVAL)
		setLocalPeriod(period)
	}, [period])

	const {
		data: dataPsychographics = [],
		isFetching: isFetchingPsyschographics,
		error: errorDataChartPsyschographics,
		isError: isErrorInDataChartPsyschographics,
		refetch: refetchDataChartPsyschographics,
	} = useFetchData<Array<CustomerProfilingTable>>(
		"retrieveCustomerProfilingTable",
		getCustomerProfilingTableMetric,
		{ period, filters, segment: PSYCHOGRAPHICS }
	)

	const {
		data: dataTimeline = dataTimelineDefault,
		isFetching: isFetchingTimeline,
		error: errorDataTimeline,
		isError: isErrorInDataTimeline,
		refetch: refetchDataTimeline,
	} = useFetchData<CustomerProfilingTimeline>(
		"retrieveCustomerProfilingTimeline",
		getCustomerProfilingTimelineMetric,
		{
			period: localPeriod,
			metric,
			filters,
			interval,
			segment: PSYCHOGRAPHICS,
		},
		{
			enabled: true,
			retry: false,
		}
	)

	const validateDataChartInfo = () =>
		isFetchingPsyschographics || dataPsychographics.length

	const onRetry = () => {
		refetchDataChartPsyschographics()
		refetchDataTimeline()
	}

	return (
		<AsyncMessageResponseHandler
			hasData={validateDataChartInfo()}
			error={errorDataChartPsyschographics || errorDataTimeline}
			isError={isErrorInDataChartPsyschographics || isErrorInDataTimeline}
			isLoading={isFetchingPsyschographics}
			retry={onRetry}
			className="benchmark-error-container"
		>
			<div className="chart-container">
				<ChartContainer
					title={{
						id:
							"phronesis.customerbehavior.application.customerProfiling.habits.title",
					}}
				>
					<Cards />
				</ChartContainer>
			</div>
			<div className="chart-container">
				<ChartContainer
					title={{
						id:
							"phronesis.customerbehavior.application.customerProfiling.metricsBySegments.title.psychographics",
					}}
					body={{
						id:
							"phronesis.customerbehavior.application.customerProfiling.metricsBySegments.description.segment",
						values: {
							segment: intl.formatMessage({
								id:
									"phronesis.customerbehavior.application.customerProfiling.metricsBySegments.description.segment.psychographics",
							}),
						},
					}}
					isLoading={isFetchingPsyschographics}
				>
					<MetricsBySegments
						data={dataPsychographics}
						metricSegment={PSYCHOGRAPHICS}
						setMetricSegment={() => {}}
						segmentSelected={PSYCHOGRAPHICS}
						isIndividual={true}
					/>
				</ChartContainer>
			</div>
			<Timeline
				data={dataTimeline}
				format={formatHabits}
				title={{ id: placeholderText.psychographicsChart }}
				subtitle={{ id: placeholderText.psychographicsChartDescription }}
				isLoading={isFetchingTimeline}
				tooltip={<Tooltip />}
				interval={interval}
				setInterval={setInterval}
				body={
					<div className="select-segment">
						<Select
							id="select-segment"
							options={options(intl)}
							label={intl.formatMessage({
								id:
									"phronesis.customerbehavior.application.customerProfiling.timeline.select.title",
							})}
							value={metric}
							onChange={(option) => setMetric(option.code)}
						/>
					</div>
				}
			/>
		</AsyncMessageResponseHandler>
	)
}

export default HabitsSegmentation
